import React, { useState, useEffect } from "react";
import axios from "axios";
import { ZoomIn, Eye, Sparkles } from "lucide-react";

export default function Gallery() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState("Toate");
  const [lightboxImage, setLightboxImage] = useState(null);

  const categories = [
    "Toate",
    "Nunți",
    "Botezuri",
    "Aniversări",
    "Evenimente Corporate",
    "Decoruri",
    "Atmosferă și lumini"
  ];

  // Backup static items in case API is empty or down
  const backupItems = [
    { id: "1", url: "https://customer-assets.emergentagent.com/job_luxury-events-sibiu/artifacts/6ej9lec9_5C615569-3220-4EAD-A3A2-26E7CB1F8B5F.png", category: "Atmosferă și lumini" },
    { id: "2", url: "https://customer-assets.emergentagent.com/job_luxury-events-sibiu/artifacts/z39h29j3_IMG_8876.jpeg", category: "Decoruri" },
    { id: "3", url: "https://customer-assets.emergentagent.com/job_luxury-events-sibiu/artifacts/wog3bf6m_6BBAA16D-AD7B-409A-8AEA-5AF8CFAA0883.png", category: "Nunți" }
  ];

  const fetchGallery = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/gallery`);
      if (response.data && response.data.length > 0) {
        setItems(response.data);
      } else {
        setItems(backupItems);
      }
    } catch (e) {
      console.error("Errored fetching gallery items:", e);
      setItems(backupItems);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGallery();
  }, []);

  const filteredItems = activeFilter === "Toate" 
    ? items 
    : items.filter(item => item.category === activeFilter);

  return (
    <section 
      id="galerie"
      data-testid="gallery-section"
      className="py-24 sm:py-32 bg-[#0A0A0A] relative"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="text-center space-y-4 mb-16">
          <span className="text-xs font-semibold tracking-[0.3em] text-[#D4AF37] uppercase flex items-center justify-center gap-2">
            <Sparkles size={12} />
            GALERIE FOTO & VIDEO
          </span>
          <h2 
            data-testid="gallery-title"
            className="font-serif-lux text-3xl sm:text-5xl text-white font-medium tracking-tight"
          >
            Momente de Elysium
          </h2>
          <div className="w-24 h-[1px] bg-[#D4AF37]/50 mx-auto mt-4" />
        </div>

        {/* Filter Categories */}
        <div 
          data-testid="gallery-filter-container"
          className="flex flex-wrap items-center justify-center gap-2 sm:gap-4 mb-12"
        >
          {categories.map((category) => (
            <button
              key={category}
              data-testid={`gallery-filter-btn-${category.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setActiveFilter(category)}
              className={`text-xs font-semibold tracking-widest uppercase px-5 py-3 transition-all duration-300 border rounded-none ${
                activeFilter === category
                  ? "bg-[#D4AF37] text-black border-[#D4AF37]"
                  : "bg-transparent text-white/70 border-white/10 hover:border-[#D4AF37] hover:text-[#D4AF37]"
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Gallery Bento Grid / Asymmetric Layout */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 animate-pulse">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="h-64 bg-white/5 border border-white/10" />
            ))}
          </div>
        ) : (
          <div 
            data-testid="gallery-grid"
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {filteredItems.map((item, index) => (
              <div
                key={item.id || index}
                data-testid={`gallery-item-${item.category.toLowerCase().replace(/\s+/g, "-")}-${index}`}
                onClick={() => setLightboxImage(item.url)}
                className="group relative h-72 overflow-hidden border border-white/10 bg-black cursor-pointer transform hover:-translate-y-1 transition-all duration-500"
              >
                {/* Image */}
                <img
                  src={item.url}
                  alt={item.category}
                  className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                />

                {/* Elegant overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/35 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-6 z-10" />

                {/* Content on hover */}
                <div className="absolute inset-x-0 bottom-0 p-6 z-20 transform translate-y-4 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-center justify-between">
                  <div>
                    <span className="text-[10px] tracking-[0.2em] text-[#D4AF37] uppercase font-medium">
                      {item.category}
                    </span>
                    <h4 className="font-serif-lux text-lg text-white mt-1">
                      Elysium Events Hall
                    </h4>
                  </div>
                  <div className="p-3 bg-[#D4AF37] text-black">
                    <Eye size={16} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Lightbox Modal */}
      {lightboxImage && (
        <div 
          data-testid="gallery-lightbox"
          onClick={() => setLightboxImage(null)}
          className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4 cursor-zoom-out"
        >
          <div className="max-w-4xl max-h-[85vh] relative">
            <img
              src={lightboxImage}
              alt="Mărire imagine"
              className="max-w-full max-h-[85vh] object-contain border border-[#D4AF37]/30"
            />
            <span className="absolute -bottom-8 left-1/2 transform -translate-x-1/2 text-xs text-white/50 tracking-wider">
              Apasă oriunde pentru a închide
            </span>
          </div>
        </div>
      )}
    </section>
  );
}
